<template>
  <div class="map" :id="mapId" ref="mapRef"></div>
</template>

<script setup lang="ts">
import { v4 as uuid } from 'uuid';
import { onMounted, ref, watch } from 'vue';

import { getGoogleMapsLoader } from '../../configs/google-api.config';
import { Coordinates } from './coordinates';

export interface MapProps {
  coordinates?: Coordinates;
}

const defaultCoordinates: Coordinates = {
  latitude: 39.8283,
  longitude: -98.5795,
};

const mapId = `map-${uuid()}`;
const mapRef = ref<HTMLElement>();
const props = defineProps<MapProps>();
let map: google.maps.Map;
let marker: google.maps.marker.AdvancedMarkerElement;

onMounted(() => {
  initializeMap();
});

watch(
  () => props.coordinates,
  (newCoords?: Coordinates) => {
    if (newCoords && newCoords.latitude && newCoords.longitude) {
      updateMapPosition(newCoords);
    }
  },
);

async function updateMapPosition(coordinates: Coordinates): Promise<void> {
  const position = new google.maps.LatLng(coordinates.latitude, coordinates.longitude);
  map.setCenter(position);
  map.setZoom(15);
  marker.position = position;
}

async function initializeMap(): Promise<void> {
  const { Map } = await getGoogleMapsLoader().importLibrary('maps');
  const { AdvancedMarkerElement } = await getGoogleMapsLoader().importLibrary('marker');
  let position: google.maps.LatLng;
  let zoom: number;

  if (!props.coordinates || !props.coordinates.latitude || !props.coordinates.longitude) {
    position = new google.maps.LatLng(defaultCoordinates.latitude, defaultCoordinates.longitude);
    zoom = 2;
  } else {
    position = new google.maps.LatLng(props.coordinates.latitude, props.coordinates.longitude);
    zoom = 15;
  }

  map = new Map(mapRef.value!, {
    mapId,
    zoom,
    center: position,
    disableDefaultUI: true,
    gestureHandling: 'none',
    clickableIcons: false,
  });

  const iconImage = document.createElement('img');
  iconImage.src = '/assets/map-pin.svg';
  iconImage.width = 96;
  iconImage.height = 96;
  iconImage.style.transform = 'translateY(50%)';

  marker = new AdvancedMarkerElement({
    map,
    position: map.getCenter(),
    content: iconImage,
  });
}
</script>

<style scoped lang="scss">
.map {
  width: 100%;
  aspect-ratio: 1 / 0.5;
  border-radius: var(--t-border-radius-sm);
}
</style>
